import { cn } from './cn.js';
import { Tooltip } from './Overlays.js';

/**
 * SLA ring.
 *
 * A badge tells you the state; a ring tells you *how close* — which is the
 * question an agent scanning forty rows is actually asking. The arc is the
 * share of the resolution budget consumed, so a row at 40% and a row at 95%
 * are distinguishable at a glance without reading a number.
 *
 * Colour is never the only signal: the ring is paired with a text figure, and
 * a breached ring is also full, so it reads in greyscale.
 */
export function SlaRing({
  consumed,
  breached,
  paused,
  verdict,
  size = 22,
  label,
}: {
  /** 0..1+ share of the resolution budget used. */
  consumed: number | null;
  breached: boolean;
  paused: boolean;
  /**
   * The settled verdict, where there is one.
   *
   * A finished ticket's ring is drawn full whatever happened, so the fill
   * cannot distinguish "met it comfortably" from "ran past the target" — and
   * without this the percentage thresholds painted every met ticket amber.
   * Where a verdict exists it decides; the thresholds keep deciding the live
   * cases.
   */
  verdict?: 'met' | 'missed' | 'live' | 'cancelled' | 'no-target';
  size?: number;
  label?: string;
}) {
  const stroke = 2.75;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  const pctRaw = breached ? 1 : Math.max(0, Math.min(1, consumed ?? 0));
  const dash = c * pctRaw;

  const settled = verdict === 'met' || verdict === 'missed';

  const tone = settled
    ? verdict === 'met'
      ? 'stroke-ok'
      : 'stroke-danger'
    : breached
      ? 'stroke-danger'
      : paused
        ? 'stroke-ink-tertiary'
        : pctRaw >= 0.8
          ? 'stroke-warn'
          : 'stroke-ok';

  const text = settled
    ? verdict === 'met'
      ? 'Met the target'
      : 'Missed the target'
    : breached
      ? 'Overdue'
      : paused
        ? 'Paused'
        : consumed === null
          ? 'No target'
          // Only meaningful while the clock is running. On a finished ticket
          // the ring is full by convention, and reporting that as "100% of
          // target used" was telling people something untrue about work that
          // took six minutes.
          : `${Math.round(pctRaw * 100)}% of target used`;

  return (
    <Tooltip content={label ?? text}>
      <span className="inline-flex items-center gap-1.5">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className={cn('shrink-0', paused && 'opacity-60')}
          role="img"
          aria-label={text}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            className="stroke-line"
          />
          {pctRaw > 0 && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${dash} ${c}`}
              // Start at 12 o'clock and run clockwise.
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              className={cn(tone, 'transition-[stroke-dasharray] duration-500 ease-out')}
            />
          )}
          {breached && (
            <circle cx={size / 2} cy={size / 2} r={2} className="fill-danger" />
          )}
          {paused && (
            <g className="fill-ink-tertiary">
              <rect x={size / 2 - 2.6} y={size / 2 - 3} width="1.7" height="6" rx="0.6" />
              <rect x={size / 2 + 0.9} y={size / 2 - 3} width="1.7" height="6" rx="0.6" />
            </g>
          )}
        </svg>
      </span>
    </Tooltip>
  );
}

/**
 * Inline sparkline. No axes and no labels — it exists to give a headline
 * number a direction, which a single figure cannot do on its own.
 */
export function Sparkline({
  points,
  width = 68,
  height = 20,
  tone = 'brand',
  className,
}: {
  points: number[];
  width?: number;
  height?: number;
  tone?: 'brand' | 'warn' | 'danger' | 'ok' | 'muted';
  className?: string;
}) {
  if (points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const pad = 2;

  const x = (i: number) => (i / (points.length - 1)) * (width - pad * 2) + pad;
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);

  const line = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${height} L${x(0).toFixed(1)},${height} Z`;

  const strokeTone = {
    brand: 'stroke-brand',
    warn: 'stroke-warn',
    danger: 'stroke-danger',
    ok: 'stroke-ok',
    muted: 'stroke-ink-tertiary',
  }[tone];
  const fillTone = {
    brand: 'fill-brand/10',
    warn: 'fill-warn/10',
    danger: 'fill-danger/10',
    ok: 'fill-ok/10',
    muted: 'fill-ink-tertiary/10',
  }[tone];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('shrink-0 overflow-visible', className)}
      aria-hidden
    >
      <path d={area} className={fillTone} />
      <path
        d={line}
        fill="none"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
        className={strokeTone}
      />
      <circle cx={x(points.length - 1)} cy={y(points.at(-1)!)} r="1.9" className={cn(strokeTone, 'fill-page')} strokeWidth="1.4" />
    </svg>
  );
}

/**
 * A compact horizontal bar for a 0..1 value, with the figure beside it.
 * Used wherever a share needs to be comparable down a column.
 */
export function Bar({
  value,
  tone = 'brand',
  width = 56,
  showValue = true,
  className,
}: {
  value: number | null;
  tone?: 'brand' | 'warn' | 'danger' | 'ok' | 'muted';
  width?: number;
  showValue?: boolean;
  className?: string;
}) {
  if (value === null) return <span className="text-2xs text-ink-tertiary">—</span>;

  const bg = {
    brand: 'bg-brand',
    warn: 'bg-warn',
    danger: 'bg-danger',
    ok: 'bg-ok',
    muted: 'bg-ink-tertiary',
  }[tone];

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <span
        className="h-1.5 shrink-0 overflow-hidden rounded-full bg-surface-sunken ring-1 ring-inset ring-line/60"
        style={{ width }}
      >
        <span
          className={cn('block h-full rounded-full transition-[width] duration-500 ease-out', bg)}
          style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }}
        />
      </span>
      {showValue && (
        <span className="nums text-xs font-medium tabular-nums">{Math.round(value * 100)}%</span>
      )}
    </span>
  );
}
